import * as React from 'react';
import cn from '../../../utils/cn';
import { IconClose } from '../../../icons';
import invariant from 'ts-invariant';
import { useMemo } from 'react';

export enum InputVariant {
  DEFAULT,
  SECONDARY,
}

export enum InputAddonSide {
  LEFT,
  RIGHT,
}

export type InputProps = React.ComponentProps<'input'> & {
  variant?: InputVariant;
};

export type AddonProps = React.ComponentPropsWithoutRef<'div'> & {
  side?: InputAddonSide;
};

const InputContext = React.createContext<Omit<InputProps, 'className'> | undefined>(
  undefined,
);

export const useInput = () => {
  const context = React.useContext(InputContext);
  invariant(context, 'useInput must be used within an InputContext component');
  return context;
};

const Root = ({
  children,
  className,
  variant = InputVariant.DEFAULT,
  ...props
}: InputProps) => {
  const value = React.useMemo(() => ({ ...props, variant }), [props, variant]);

  return (
    <InputContext.Provider value={value}>
      <div
        className={cn(
          'group/input',
          'flex h-8 w-full items-center overflow-hidden rounded border-0 bg-white antialiased ring-1 ring-inset ring-neutral-300',
          'has-[input[data-invalid=true]]:ring-red-500',
          { 'bg-neutral-100': variant === InputVariant.SECONDARY },
          className,
        )}
      >
        {children}
      </div>
    </InputContext.Provider>
  );
};

const Clear = React.memo(
  React.forwardRef<
    HTMLButtonElement,
    Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>
  >(function ButtonClear({ className, ...props }, ref) {
    return (
      <button
        {...props}
        className={cn('mr-2.5 shrink-0', className)}
        aria-label="Remise à zéro"
        ref={ref}
        tabIndex={-1}
        type="button" // Explicit to avoid form submission
      >
        <IconClose className="h-2.5 w-2.5 fill-neutral-300 duration-300 hover:fill-neutral-700" />
      </button>
    );
  }),
);

const InputField = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant: propsVariant, ...props }, ref) => {
    const { variant: contextVariant, ...inputContext } = useInput();

    const variant = useMemo(
      () => propsVariant || contextVariant,
      [propsVariant, contextVariant],
    );

    return (
      <input
        className={cn(
          'no-spinner no-search-decoration w-full rounded-none border-0 bg-transparent px-3 py-1 text-sm font-medium text-neutral-700 ring-0',
          'placeholder:text-xs placeholder:text-neutral-300 placeholder:antialiased',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'focus:outline-0 focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
          { 'placeholder:text-neutral-500': variant === InputVariant.SECONDARY },
          className,
        )}
        ref={ref}
        {...props}
        {...inputContext}
      />
    );
  },
);

const Addon = React.memo(
  React.forwardRef<HTMLDivElement, AddonProps>(function Button(
    { className, children, side = InputAddonSide.RIGHT, ...props },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          'grid h-full shrink-0 place-items-center overflow-hidden rounded border border-neutral-300 px-2.5',
          'group-has-[input[data-invalid=true]]/input:border-red-500',
          '[&_svg]:size-4 [&_svg]:text-neutral-700',
          {
            'rounded-r-none border-r-0': side === InputAddonSide.LEFT,
            'rounded-l-none border-l-0': side === InputAddonSide.RIGHT,
          },
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }),
);

const Button = React.memo(
  React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'>>(
    function Button({ className, children, type = 'button', ...props }, ref) {
      return (
        <button
          ref={ref}
          type={type}
          className={cn(
            'flex h-full shrink-0 items-center justify-center overflow-hidden rounded border border-neutral-300 bg-white px-2.5',
            '[&_svg]:h-2.5 [&_svg]:w-2.5 [&_svg]:fill-neutral-700',
            'hover:border-neutral-500',
            'group-has-[input[data-invalid=true]]/input:border-red-500',
            'group-has-[input[data-invalid=true]]/input:hover:bg-neutral-50',
            className,
          )}
          {...props}
        >
          {children}
        </button>
      );
    },
  ),
);

const Error = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  return (
    <p ref={ref} className={cn('text-xs font-normal text-red-500', className)} {...props}>
      {children}
    </p>
  );
});

interface CompoundInput {
  Root: typeof Root;
  Input: typeof InputField;
  Clear: typeof Clear;
  Addon: typeof Addon;
  Button: typeof Button;
  Error: typeof Error;
}

type InputComponent = React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
> &
  CompoundInput;

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  return (
    <Root {...props}>
      <InputField ref={ref} />
    </Root>
  );
}) as InputComponent;

Root.displayName = 'Input.Root';
InputField.displayName = 'Input.Input';
Clear.displayName = 'Input.Clear';
Addon.displayName = 'Input.Addon';
Button.displayName = 'Input.Button';
Error.displayName = 'Input.Error';

Input.Root = Root;
Input.Input = InputField;
Input.Clear = Clear;
Input.Addon = Addon;
Input.Button = Button;
Input.Error = Error;

export default Input;
